import { useCallback, useEffect, useState, type FormEvent } from 'react';

interface PilotData {
  readonly program: Record<string, unknown> | null;
  readonly operations: readonly Record<string, unknown>[];
  readonly assessments: readonly Record<string, unknown>[];
  readonly divergences: readonly Record<string, unknown>[];
  readonly metrics: {
    readonly shadowCompleted: number;
    readonly averageCompleteness: number;
    readonly spreadsheetDependencyPercent: number;
    readonly divergencesOpen: number;
  };
  readonly gates: Record<string, boolean>;
  readonly canAccept: boolean;
}

const gateLabels: Record<string, string> = {
  procedureMapped: 'Procedimento vigente mapeado',
  dataProtectionDefined: 'Responsável LGPD definido',
  rollbackTested: 'Plano de retorno testado',
  fifteenShadowOperations: '15 operações em modo sombra',
  zeroCriticalFailures: 'Zero falha crítica conhecida',
  divergencesRetested: 'Divergências retestadas',
};

function text(row: Record<string, unknown> | null, key: string): string {
  const value = row?.[key];
  return typeof value === 'string' ? value : '';
}

export function PilotReadiness({
  organizationId,
  onExpiredSession,
}: {
  readonly organizationId: string;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [data, setData] = useState<PilotData>();
  const [procedureReference, setProcedureReference] = useState('');
  const [operatorResponsible, setOperatorResponsible] = useState('');
  const [dataProtectionContact, setDataProtectionContact] = useState('');
  const [rollbackTested, setRollbackTested] = useState(false);
  const [operationId, setOperationId] = useState('');
  const [assessmentId, setAssessmentId] = useState(() => crypto.randomUUID());
  const [completeness, setCompleteness] = useState('100');
  const [coordinationMinutes, setCoordinationMinutes] = useState('');
  const [spreadsheetRequired, setSpreadsheetRequired] = useState(true);
  const [hardConflictMissed, setHardConflictMissed] = useState(false);
  const [decisionAttributionMissing, setDecisionAttributionMissing] = useState(false);
  const [crossTenantIncident, setCrossTenantIncident] = useState(false);
  const [assessmentNotes, setAssessmentNotes] = useState(
    'Operação acompanhada em paralelo ao procedimento vigente.',
  );
  const [category, setCategory] = useState('data');
  const [divergenceId, setDivergenceId] = useState(() => crypto.randomUUID());
  const [impact, setImpact] = useState('');
  const [cause, setCause] = useState('');
  const [correction, setCorrection] = useState('');
  const [retestStatus, setRetestStatus] = useState('pending');
  const [acceptanceSigner, setAcceptanceSigner] = useState('');
  const [acceptanceNote, setAcceptanceNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/pilot`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw new Error(String(response.status));
      const result = (await response.json()) as PilotData;
      setData(result);
      setError(undefined);
      if (result.program) {
        setProcedureReference(text(result.program, 'procedure_reference'));
        setOperatorResponsible(text(result.program, 'operator_responsible'));
        setDataProtectionContact(text(result.program, 'data_protection_contact'));
        setRollbackTested(Boolean(result.program.rollback_tested_at));
      }
      const firstOperation = result.operations[0];
      if (firstOperation) {
        setOperationId((current) => current || text(firstOperation, 'id'));
      }
    } catch {
      setError('Não foi possível carregar as evidências do piloto.');
    }
  }, [onExpiredSession, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const command = async (payload: object, success: string) => {
    setBusy(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/pilot`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        await onExpiredSession();
        return false;
      }
      if (!response.ok) {
        setError(
          response.status === 409
            ? 'Os portões do piloto ainda não permitem esta ação.'
            : 'Revise os dados informados.',
        );
        return false;
      }
      setData((await response.json()) as PilotData);
      setFeedback(success);
      return true;
    } catch {
      setError('Não foi possível salvar a evidência. Os campos foram preservados.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const configure = (event: FormEvent) => {
    event.preventDefault();
    void command(
      {
        action: 'configure',
        programId: text(data?.program ?? null, 'id') || crypto.randomUUID(),
        procedureReference,
        operatorResponsible,
        dataProtectionContact,
        rollbackTested,
      },
      'Configuração do piloto atualizada.',
    );
  };
  const assess = (event: FormEvent) => {
    event.preventDefault();
    void command(
      {
        action: 'assess',
        assessmentId,
        operationId,
        shadowCompleted: true,
        hardConflictMissed,
        decisionAttributionMissing,
        crossTenantIncident,
        spreadsheetRequired,
        completenessPercent: Number(completeness),
        coordinationMinutes: coordinationMinutes ? Number(coordinationMinutes) : null,
        notes: assessmentNotes,
      },
      'Operação adicionada às evidências do piloto.',
    ).then((saved) => {
      if (saved) setAssessmentId(crypto.randomUUID());
    });
  };
  const divergence = async (event: FormEvent) => {
    event.preventDefault();
    const saved = await command(
      {
        action: 'divergence',
        divergenceId,
        operationId: operationId || null,
        category,
        impact,
        cause,
        correction,
        retestStatus,
      },
      'Divergência registrada.',
    );
    if (saved) {
      setDivergenceId(crypto.randomUUID());
      setImpact('');
      setCause('');
      setCorrection('');
    }
  };
  const accept = (event: FormEvent) => {
    event.preventDefault();
    void command(
      { action: 'accept', acceptanceSigner, acceptanceNote },
      'Aceite formal registrado com autoria e instante.',
    );
  };

  const download = () => {
    if (!data) return;
    const url = URL.createObjectURL(
      new Blob(
        [
          JSON.stringify(
            { generatedAt: new Date().toISOString(), organizationId, ...data },
            null,
            2,
          ),
        ],
        { type: 'application/json' },
      ),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `piloto-rbac135-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="pilotReadiness" aria-labelledby="pilot-title">
      <header className="setupHeader">
        <div>
          <span className="sectionEyebrow">Piloto em modo sombra</span>
          <h2 id="pilot-title">Aceite RBAC 135</h2>
          <p>Reúna evidências reais antes de retirar a planilha do escopo homologado.</p>
        </div>
        <button className="secondaryButton" disabled={!data} type="button" onClick={download}>
          Baixar evidências
        </button>
      </header>
      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : null}
      {feedback ? (
        <p className="invitationFeedback invitationFeedback-success" role="status">
          {feedback}
        </p>
      ) : null}
      {data ? (
        <>
          <div className="pilotMetrics">
            <article>
              <span>Operações sombra</span>
              <strong>{data.metrics.shadowCompleted}/15</strong>
            </article>
            <article>
              <span>Completude média</span>
              <strong>{data.metrics.averageCompleteness}%</strong>
            </article>
            <article>
              <span>Dependência de planilha</span>
              <strong>{data.metrics.spreadsheetDependencyPercent}%</strong>
            </article>
            <article>
              <span>Divergências abertas</span>
              <strong>{data.metrics.divergencesOpen}</strong>
            </article>
          </div>
          <ul className="pilotGates">
            {Object.entries(data.gates).map(([key, passed]) => (
              <li className={passed ? 'isComplete' : ''} key={key}>
                <span>{passed ? '✓' : '○'}</span>
                {gateLabels[key] ?? key}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <details open={!data?.program}>
        <summary>1. Configurar responsáveis e procedimento</summary>
        <form className="pilotForm" onSubmit={configure}>
          <label>
            Referência do procedimento
            <input
              required
              minLength={3}
              value={procedureReference}
              onChange={(e) => setProcedureReference(e.target.value)}
            />
          </label>
          <label>
            Responsável operacional do operador
            <input
              required
              minLength={3}
              value={operatorResponsible}
              onChange={(e) => setOperatorResponsible(e.target.value)}
            />
          </label>
          <label>
            Contato de proteção de dados
            <input
              required
              minLength={3}
              value={dataProtectionContact}
              onChange={(e) => setDataProtectionContact(e.target.value)}
            />
          </label>
          <label className="checkField">
            <input
              type="checkbox"
              checked={rollbackTested}
              onChange={(e) => setRollbackTested(e.target.checked)}
            />
            Plano de retorno testado
          </label>
          <button className="primaryButton" disabled={busy} type="submit">
            Salvar configuração
          </button>
        </form>
      </details>
      <details>
        <summary>2. Registrar operação acompanhada</summary>
        <form className="pilotForm" onSubmit={assess}>
          <label>
            Operação comercial encerrada
            <select required value={operationId} onChange={(e) => setOperationId(e.target.value)}>
              <option value="">Selecione</option>
              {data?.operations.map((op) => (
                <option key={text(op, 'id')} value={text(op, 'id')}>
                  {text(op, 'reference_code')} · {text(op, 'mission_name')}
                </option>
              ))}
            </select>
          </label>
          <label>
            Completude (%)
            <input
              required
              min="0"
              max="100"
              type="number"
              value={completeness}
              onChange={(e) => setCompleteness(e.target.value)}
            />
          </label>
          <label>
            Tempo de coordenação (min)
            <input
              min="0"
              max="10080"
              type="number"
              value={coordinationMinutes}
              onChange={(e) => setCoordinationMinutes(e.target.value)}
            />
          </label>
          <label className="checkField">
            <input
              type="checkbox"
              checked={spreadsheetRequired}
              onChange={(e) => setSpreadsheetRequired(e.target.checked)}
            />
            Ainda dependeu da planilha
          </label>
          <label className="checkField pilotFailure">
            <input
              type="checkbox"
              checked={hardConflictMissed}
              onChange={(e) => setHardConflictMissed(e.target.checked)}
            />
            Conflito duro não sinalizado
          </label>
          <label className="checkField pilotFailure">
            <input
              type="checkbox"
              checked={decisionAttributionMissing}
              onChange={(e) => setDecisionAttributionMissing(e.target.checked)}
            />
            Decisão sem ator/autoridade
          </label>
          <label className="checkField pilotFailure">
            <input
              type="checkbox"
              checked={crossTenantIncident}
              onChange={(e) => setCrossTenantIncident(e.target.checked)}
            />
            Acesso cruzado detectado
          </label>
          <label className="fieldWide">
            Notas
            <textarea
              required
              minLength={3}
              value={assessmentNotes}
              onChange={(e) => setAssessmentNotes(e.target.value)}
            />
          </label>
          <button
            className="primaryButton"
            disabled={busy || !data?.program || !operationId}
            type="submit"
          >
            Registrar acompanhamento
          </button>
        </form>
      </details>
      <details>
        <summary>3. Registrar e retestar divergência</summary>
        <form className="pilotForm" onSubmit={(event) => void divergence(event)}>
          <label>
            Categoria
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="data">Dados</option>
              <option value="rule">Regra</option>
              <option value="time">Tempo</option>
              <option value="responsibility">Responsabilidade</option>
            </select>
          </label>
          <label>
            Status do reteste
            <select value={retestStatus} onChange={(e) => setRetestStatus(e.target.value)}>
              <option value="pending">Pendente</option>
              <option value="passed">Aprovado</option>
              <option value="failed">Reprovado</option>
            </select>
          </label>
          <label className="fieldWide">
            Impacto
            <textarea
              required
              minLength={3}
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
            />
          </label>
          <label className="fieldWide">
            Causa
            <textarea
              required
              minLength={3}
              value={cause}
              onChange={(e) => setCause(e.target.value)}
            />
          </label>
          <label className="fieldWide">
            Correção
            <textarea
              required
              minLength={3}
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
            />
          </label>
          <button className="primaryButton" disabled={busy || !data?.program} type="submit">
            Registrar divergência
          </button>
        </form>
      </details>
      <details>
        <summary>4. Aceite da gestão operacional</summary>
        <form className="pilotForm" onSubmit={accept}>
          <label>
            Responsável que assina o aceite
            <input
              required
              minLength={3}
              value={acceptanceSigner}
              onChange={(e) => setAcceptanceSigner(e.target.value)}
            />
          </label>
          <label className="fieldWide">
            Declaração do escopo aceito
            <textarea
              required
              minLength={10}
              value={acceptanceNote}
              onChange={(e) => setAcceptanceNote(e.target.value)}
            />
          </label>
          <button className="primaryButton" disabled={busy || !data?.canAccept} type="submit">
            Registrar aceite formal
          </button>
          {!data?.canAccept ? (
            <small>Disponível quando todos os portões acima estiverem aprovados.</small>
          ) : null}
        </form>
      </details>
      {data?.program?.accepted_at ? (
        <div className="setupComplete">
          <div>
            <strong>Piloto aceito por {text(data.program, 'acceptance_signer')}</strong>
            <span>
              {new Date(text(data.program, 'accepted_at')).toLocaleString('pt-BR')} · o escopo pode
              seguir ao corte aprovado.
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}
